// Paint inspections list. Current Project (location tabs) is the default; All Projects loads last N days on demand.
import React, { useEffect, useState, Fragment } from "react";
import { connect } from "react-redux";
import { Row, Col, Button, Card, CardHeader, CardBody, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { Link } from "react-router-dom";
import qs from "query-string";
import classnames from "classnames";

import NotAuthorized from "modules/5_view_helpers/web/NotAuthorized";
import HasRole from "modules/5_view_helpers/HasRole";
import { parseDays } from "modules/5_view_helpers/web/DaysSelector";
import TableTorch from "modules/5_view_helpers/web/TableTorch";

import { getProjectQaPaintInspections } from "../actions";
import { toggleRightSidebarOpen } from "modules/3_reducers/web/ThemeOptions";
import { getPaintLocations } from "modules/paint/locations/actions";
import paintInspectionColumns, { sectionReadingsChild } from "./columns";
import AllProjectsPaintInspections from "./all_projects";

function mapStateToProps(state, props) {
  const project_id = props.match?.params?.project_id || props.project_id;

  return {
    project: state.projects[project_id],
    this_project_qa_paint_inspections: _.filter(state.project_qa_paint_inspections, (pi) => pi.project_id == project_id),
    rightSidebarOpen: state.ThemeOptions.rightSidebarOpen,
    paint_locations: state.paint_locations,
  };
}

const ProjectQaPaintInspectionsIndex = (props) => {
  const { showNewButton, showSelection, onSelected, history, location } = props;
  const project_id = props.match?.params?.project_id || props.project_id;
  const showProjectTabs = !showSelection && !!history;

  const query = qs.parse(location?.search || "");
  const projectTab = showProjectTabs && query.tab == "all_projects" ? "all_projects" : "current_project";
  const days = parseDays(query.days);

  const setProjectTab = (tab) => {
    const next = { tab };
    if (tab == "all_projects") next.days = days;
    history.push({ pathname: location.pathname, search: qs.stringify(next) });
  };

  const setDays = (nextDays) => {
    history.replace({ pathname: location.pathname, search: qs.stringify({ tab: "all_projects", days: nextDays }) });
  };

  if (!showProjectTabs) {
    return <CurrentProjectPaintInspections project_id={project_id} showNewButton={showNewButton} showSelection={showSelection} onSelected={onSelected} {...props} />;
  }

  return (
    <div>
      <Nav tabs>
        <NavItem>
          <NavLink
            className={classnames({ active: projectTab == "current_project" })}
            onClick={() => setProjectTab("current_project")}
          >
            Current Project
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink className={classnames({ active: projectTab == "all_projects" })} onClick={() => setProjectTab("all_projects")}>
            All Projects
          </NavLink>
        </NavItem>
      </Nav>
      <TabContent activeTab={projectTab}>
        <TabPane tabId="current_project">
          {projectTab == "current_project" && (
            <CurrentProjectPaintInspections
              project_id={project_id}
              showNewButton={showNewButton}
              showSelection={showSelection}
              onSelected={onSelected}
              {...props}
            />
          )}
        </TabPane>
        <TabPane tabId="all_projects">
          {projectTab == "all_projects" && <AllProjectsPaintInspections days={days} onDaysChange={setDays} />}
        </TabPane>
      </TabContent>
    </div>
  );
};

const CurrentProjectPaintInspections = (props) => {
  const [selected, setSelected] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const { paint_locations, showNewButton, showSelection, onSelected } = props;
  const project_id = props.match?.params?.project_id || props.project_id;

  useEffect(() => {
    if (showSelection && onSelected) {
      onSelected(selected);
    }
  }, [selected]);

  useEffect(() => {
    props.getPaintLocations();
  }, []);

  useEffect(() => {
    if (_.isEmpty(activeTab) && _.isNotEmpty(paint_locations)) {
      const findDefault = _.find(paint_locations, (pl) => pl.default);
      setActiveTab(findDefault?.id);
    }
  }, [paint_locations]);

  const { this_project_qa_paint_inspections } = props;
  useEffect(() => {
    if (project_id && _.isEmpty(this_project_qa_paint_inspections)) {
      props.getProjectQaPaintInspections(project_id);
    }
  }, [project_id]);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <div>
      <Nav tabs>
        {_.map(paint_locations, (paint_location, i) => (
          <NavItem key={i}>
            <NavLink
              className={classnames({ active: activeTab === paint_location.id })}
              onClick={() => {
                toggle(paint_location.id);
              }}
            >
              {paint_location.name}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      <TabContent activeTab={activeTab}>
        {_.map(paint_locations, (paint_location, i) => {
          const this_location_paint_inspections = _.filter(this_project_qa_paint_inspections, (pi) => pi.location_id == paint_location.id);

          return (
            <TabPane tabId={paint_location?.id} key={i}>
              {activeTab == paint_location.id && (
                <ProjectQaPaintInspectionsByLocation
                  showNewButton={showNewButton}
                  paint_inspections={this_location_paint_inspections}
                  project_id={project_id}
                  showSelection={showSelection}
                  setSelected={setSelected}
                  selected={selected}
                  {...props}
                />
              )}
            </TabPane>
          );
        })}
      </TabContent>
    </div>
  );
};

ProjectQaPaintInspectionsIndex.defaultProps = {
  showNewButton: true,
};

export default connect(mapStateToProps, { getProjectQaPaintInspections, toggleRightSidebarOpen, getPaintLocations })(
  ProjectQaPaintInspectionsIndex,
);

const ProjectQaPaintInspectionsByLocation = (props) => {
  const { project_id, project, paint_inspections, showNewButton } = props;

  const readRole = HasRole({ resource_type: "Project::Qa::PaintInspection", name: ["read", "moderator"] });
  const createRole = HasRole({ resource_type: "Project::Qa::PaintInspection", name: ["create", "moderator"] });
  const updateRole = HasRole({ resource_type: "Project::Qa::PaintInspection", name: ["update", "moderator"] });

  const ordered_paint_inspections = React.useMemo(
    () => _.orderBy(paint_inspections, ["assembly.mark", "phase", "lot", "number"], ["asc", "asc", "asc", "asc"]),
    [paint_inspections],
  );

  if (!readRole) {
    return <NotAuthorized />;
  }

  return (
    <Fragment>
      <Card>
        <CardHeader>
          <Row>
            <Col>
              <h3>{project?.display_name} Paint inspections</h3>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <Row>
            {createRole && showNewButton && (
              <Col>
                <Link to={`/project/${project_id}/qa/paint_inspections/new`}>
                  <Button color="primary" size="md">
                    <i className="fa fa-plus"> Create New Paint inspection</i>
                  </Button>
                </Link>
              </Col>
            )}
          </Row>

          {_.isNotEmpty(ordered_paint_inspections) && (
            <TableTorch
              columns={paintInspectionColumns({ updateRole })}
              data={ordered_paint_inspections}
              selectable
              showCreateReport
              reportURL="paint_inspections.pdf"
              reportModal={true}
              reportModalButtonLabel="Get Overall Report"
              initialGroupBy={[{ label: "Assembly", value: "assembly.display_name_with_guid" }]}
              childComponent={sectionReadingsChild}
            />
          )}
        </CardBody>
      </Card>
    </Fragment>
  );
};
